import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section,
} from "../../components/Primitives";
import { EmailMyResult } from "../../components/EmailMyResult";
import { getFestivals, runKundli, runNumerology, apiError } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { AnimatePresence, EASE, Reveal, Stagger, StaggerItem, motion } from "../../lib/motion";

const FACTOR = 0.91;

export function CaratRattiConverter() {
  const [carat, setCarat] = useState("3.05");
  const [ratti, setRatti] = useState("");

  useSEO({
    title: "Carat to Ratti Converter — With the Factor Stated",
    description:
      "Convert carat to ratti and back, with the conversion factor printed on the page and an explanation of why ratti conversion is not standardised across the Indian trade.",
    canonical: "/tools/carat-ratti-converter",
  });

  const fromCarat = (v) => { setCarat(v); setRatti(v ? (parseFloat(v) / FACTOR).toFixed(3) : ""); };
  const fromRatti = (v) => { setRatti(v); setCarat(v ? (parseFloat(v) * FACTOR).toFixed(3) : ""); };

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Carat–ratti converter" }]} />
      </Container>
      <Section>
        <Container className="max-w-2xl">
          <Eyebrow>With the factor stated, and the caveat</Eyebrow>
          <H1 className="mt-4">Carat–Ratti Converter</H1>
          <div className="card-s mt-8 grid gap-4 p-6 sm:grid-cols-2" data-testid="carat-ratti">
            <label className="block">
              <span className="field-label">Carat</span>
              <input type="number" step="0.01" value={carat} onChange={(e) => fromCarat(e.target.value)} className="field num" data-testid="carat-input" />
            </label>
            <label className="block">
              <span className="field-label">Ratti</span>
              <input type="number" step="0.01" value={ratti} onChange={(e) => fromRatti(e.target.value)} className="field num" data-testid="ratti-input" />
            </label>
            <p className="num sm:col-span-2 text-sm text-gold">Conversion factor used: 1 ratti = {FACTOR} carat</p>
          </div>
          <Reveal className="measure mt-10 space-y-5 text-sm leading-[1.7] text-dim">
            <p>
              <strong className="text-ink">This factor is not standardised.</strong> Factors between 0.90
              and 0.92 are in common use across the Indian trade, and some sellers quietly use whichever
              one flatters the number they want to show. On a 5 carat stone, the spread between 0.90 and
              0.92 is about a tenth of a ratti — small, until it is the basis of a per-ratti price.
            </p>
            <p>
              Every weight on this site is published in both carat and ratti with the factor printed on
              the same page, so you can always redo the arithmetic yourself.
            </p>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}

export function NumerologyCalculator() {
  const [form, setForm] = useState({ name: "", dob: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Free Numerology Calculator — Life Path, Destiny & Soul Urge",
    description:
      "A free numerology calculator that states which letter-assignment school it used, leaves master numbers unreduced, and is honest that the arithmetic is not the decision.",
    canonical: "/tools/numerology-calculator",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setResult(await runNumerology(form));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Numerology calculator" }]} />
      </Container>
      <Section>
        <Container className="max-w-2xl">
          <Eyebrow>Free, and always will be</Eyebrow>
          <H1 className="mt-4">Numerology Calculator</H1>
          <form onSubmit={submit} className="card-s mt-8 space-y-4 p-6" data-testid="numerology-form">
            <label className="block">
              <span className="field-label">Full name, as you use it</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" data-testid="numerology-name" />
            </label>
            <label className="block">
              <span className="field-label">Date of birth</span>
              <input required type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="field num" data-testid="numerology-dob" />
            </label>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="numerology-submit">
              {busy ? "Calculating…" : "Calculate"}
            </button>
          </form>

          <AnimatePresence>
            {result && (
              <motion.div
                key="numerology-result"
                className="mt-10"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.65, ease: EASE }}
                data-testid="numerology-result"
              >
                <table className="w-full text-sm">
                  <tbody>
                    {result.results.map((r) => (
                      <tr key={r.label} className="border-b border-rule">
                        <th scope="row" className="ui py-4 pr-4 text-left font-medium text-dim">
                          {r.label}
                          <span className="block text-xs font-normal opacity-80">{r.note}</span>
                        </th>
                        <td className="num py-4 text-right text-2xl text-deep">{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="ui mt-6 text-sm leading-[1.7] text-dim"><strong className="text-ink">Method.</strong> {result.method}</p>
                <p className="measure ui mt-4 border-t border-rule pt-4 text-sm leading-[1.7] text-dim">{result.honest_note}</p>
                <EmailMyResult
                  tool="numerology"
                  summary={Object.fromEntries(result.results.map((r) => [r.label, r.value]))}
                />
                <Link to="/consult/name-decision" className="btn btn-secondary mt-6" data-testid="numerology-upsell">
                  The Naming Decision — for when the arithmetic isn't the hard part
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Section>
    </>
  );
}

export function FreeKundli() {
  const [form, setForm] = useState({ name: "", dob: "", time_of_birth: "", place_of_birth: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Free Kundli — Birth Chart Basics, Declined for Under-18s",
    description:
      "A free simplified kundli from your date, time and place of birth. Charts are declined for anyone under 18 under the DPDP Act 2023, and nothing you enter here is stored.",
    canonical: "/tools/free-kundli",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setResult(await runKundli(form));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Free kundli" }]} />
      </Container>
      <Section>
        <Container className="max-w-2xl">
          <Eyebrow>Nothing you enter here is stored</Eyebrow>
          <H1 className="mt-4">Free Kundli</H1>
          <form onSubmit={submit} className="card-s mt-8 space-y-4 p-6" data-testid="kundli-form">
            <label className="block">
              <span className="field-label">Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" data-testid="kundli-name" />
            </label>
            <label className="block">
              <span className="field-label">Date of birth</span>
              <input required type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="field num" data-testid="kundli-dob" />
            </label>
            <label className="block">
              <span className="field-label">Exact time of birth</span>
              <input required type="time" value={form.time_of_birth} onChange={(e) => setForm({ ...form, time_of_birth: e.target.value })} className="field num" data-testid="kundli-time" />
            </label>
            <label className="block">
              <span className="field-label">Place of birth</span>
              <input required value={form.place_of_birth} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })} className="field" data-testid="kundli-place" />
            </label>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="kundli-submit">
              {busy ? "Calculating…" : "Generate chart"}
            </button>
          </form>

          <AnimatePresence>
            {result?.declined && (
              <motion.div
                key="kundli-declined"
                className="card-s elev mt-10 border-l-2 border-l-alert p-6 sm:p-8"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="kundli-declined"
              >
                <H2 className="h2-sub">{result.reason}</H2>
                <p className="ui measure mt-4 text-sm leading-[1.7] text-dim">{result.explanation}</p>
              </motion.div>
            )}

            {result && !result.declined && (
              <motion.div
                key="kundli-result"
                className="mt-10"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.65, ease: EASE }}
                data-testid="kundli-result"
              >
                <H2>{result.for}</H2>
                <p className="num mt-2 text-sm text-dim">
                  {result.birth.date} · {result.birth.time} · {result.birth.place}
                </p>
                <table className="mt-8 w-full text-sm">
                  <tbody>
                    {result.chart.map((r) => (
                      <tr key={r.label} className="border-b border-rule">
                        <th scope="row" className="ui py-3.5 pr-4 text-left font-medium text-dim">{r.label}</th>
                        <td className="num py-3.5 text-right text-ink">{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="measure ui mt-6 text-sm leading-[1.7] text-dim">{result.honest_note}</p>
                <p className="ui mt-3 text-xs text-verify">{result.retention}</p>
                <EmailMyResult
                  tool="free-kundli"
                  summary={Object.fromEntries(result.chart.map((c) => [c.label, c.value]))}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Section>
    </>
  );
}

export function MuhuratCalendar() {
  const { data } = useQuery({ queryKey: ["festivals"], queryFn: getFestivals });

  useSEO({
    title: "Muhurat Calendar & Festival Dates",
    description:
      "Festival dates and muhurat timings, with what is traditionally done on each — and the relevant collection attached rather than shouted.",
    canonical: "/tools/muhurat-calendar",
  });

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Muhurat calendar" }]} />
      </Container>
      <Section>
        <Container>
          <Eyebrow>Refreshed annually</Eyebrow>
          <H1 className="mt-4 max-w-3xl">Muhurat calendar and festival dates</H1>
          <Lede className="mt-5">
            The dates, the muhurat timings, and what is traditionally done — with the relevant collection
            attached at the bottom of each entry, not shouted at the top of it.
          </Lede>

          {!data && (
            <div className="mt-14 divide-y divide-rule" aria-busy="true" data-testid="muhurat-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="grid gap-4 py-7 sm:grid-cols-[170px_1fr]">
                  <div className="skeleton h-4 w-28" />
                  <div>
                    <div className="skeleton h-5 w-48" />
                    <div className="skeleton mt-3 h-3 w-40" />
                    <div className="skeleton mt-3 h-3 w-full max-w-md" />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Stagger className="mt-14 divide-y divide-rule" stagger={0.05} data-testid="muhurat-list">
            {(data?.items || []).map((f) => (
              <StaggerItem key={f.slug} as="article" className="grid gap-4 py-7 sm:grid-cols-[170px_1fr]">
                <p className="num text-sm text-gold">
                  {new Date(`${f.date}T12:00:00`).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <div>
                  <H3>{f.name}</H3>
                  <p className="num mt-1.5 text-xs text-dim">{f.muhurat}</p>
                  <p className="ui mt-3 text-sm leading-[1.7] text-dim">{f.note}</p>
                  <Link to={f.collection} className="link-u ui mt-3 inline-block text-xs">
                    Related collection →
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>
    </>
  );
}
